import { useState, useEffect, useCallback } from 'react';

export const useUIEnhancements = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Motion Design: Notification badge pulse animation
  const [showPulse, setShowPulse] = useState(false);

  // Performance Optimization: Debounced search input
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Accessibility: Focus management
  const trapFocus = (element) => {
    const focusableEls = element.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])');
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];

    element.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstFocusableEl) {
          e.preventDefault();
          lastFocusableEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastFocusableEl) {
          e.preventDefault();
          firstFocusableEl.focus();
        }
      }
    });
  };

  // Error States: Offline mode indicator
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Security: Content sanitization
  const sanitizeContent = useCallback((content) => {
    // This is a basic implementation. Consider using a library like DOMPurify for production use.
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      "/": '&#x2F;',
    };
    const reg = /[&<>"'/]/ig;
    return content.replace(reg, (match) => (map[match]));
  }, []);

  return {
    isOnline,
    showPulse,
    setShowPulse,
    debounce,
    trapFocus,
    sanitizeContent,
  };
};
